import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment-timezone';
import { Check, Copy, Pencil, Plus, Search } from 'lucide-react';
import CommonHeader from '../../util_components/common_header';
import Button from '../../util_components/button';
import CustomInput from '../../util_components/custom_input';
import CustomSelect from '../../util_components/custom_single_select';
import CustomModal from '../../util_components/custom_modal';
import { get_api_url } from '../../../utils/urls';
import { api_with_method } from '../../../redux/api_funcs';
import * as actions from '../../../redux/action_creators';
import { CLASS_PREFERENCE_OPTIONS, EMPTY_FORM, from_affiliate, to_payload } from './affiliate_form';
import './affiliates.css';

const AFFILIATES_URL = '/v2/cms_user/affiliates';

// Grouped so the long form reads in the order people think about an offer:
// what it is, what the student gets, and where it is used.
const FORM_SECTIONS = [
  {
    title: 'Basics',
    fields: [
      ['name', 'Name'],
      ['description', 'Description']
    ]
  },
  {
    title: 'Offer terms',
    fields: [
      ['credits', 'Credits', 'number'],
      ['expiry_from_activation', 'Days valid after activation', 'number'],
      ['percentage_discount', 'Percentage discount', 'number'],
      ['fixed_discount', 'Fixed discount', 'number']
    ]
  },
  {
    title: 'Funnel and package',
    fields: [
      ['funnel_url', 'Funnel URL'],
      ['funnel_type', 'Funnel type'],
      ['offer_type', 'Offer type'],
      ['lp_version', 'LP version'],
      ['package_uuid', 'Package ID (optional)']
    ]
  }
];

const CLASS_PREFERENCE_LABELS = Object.fromEntries(
  CLASS_PREFERENCE_OPTIONS.map((option) => [option.value, option.label])
);

export default function Affiliates() {
  // The page shows for roles holding /affiliates in Access Roles (grant it to
  // SUPER_USER and ENGINEERING). goserver's requireAffiliatesAccess admits only
  // SUPER_USER and ENGINEERING on every endpoint, whatever the page grants say;
  // the route gate only hides the page.
  const access_pages = useSelector((state) => state.home.access_pages);
  const has_access = (access_pages || []).includes('/affiliates');
  // Offer expiry is shown and entered in the zone picked in the header, like the
  // rest of Hridaya; see affiliate_form.js for why not the browser zone.
  const iana_timezone = useSelector((state) => state.home.iana_timezone);
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [affiliates, setAffiliates] = useState([]);
  const [search, setSearch] = useState('');
  const [editingUuid, setEditingUuid] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [copiedUuid, setCopiedUuid] = useState('');

  const notify = (type, message) =>
    dispatch(actions.set_notification_variable(true, type, message));

  const error_message = (e) =>
    e?.response?.data?.message ||
    e?.response?.data?.reason ||
    'Some error occured. Please contact dev team.';

  const load_affiliates = () => {
    setLoading(true);
    return api_with_method('get', get_api_url(AFFILIATES_URL, false, true), null, true)
      .then((res) => {
        setAffiliates(res.data.data.affiliates || []);
        setLoading(false);
      })
      .catch((e) => {
        notify('error', error_message(e));
        setLoading(false);
      });
  };

  useEffect(() => {
    if (has_access) load_affiliates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const open_create = () => {
    setEditingUuid('');
    setForm(EMPTY_FORM);
    setFormOpen(true);
  };

  const open_edit = (row) => {
    setEditingUuid(row.uuid);
    setForm(from_affiliate(row, iana_timezone));
    setFormOpen(true);
  };

  const close_form = () => {
    setFormOpen(false);
    setEditingUuid('');
  };

  const on_change = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = () => {
    const { payload, error } = to_payload(form, iana_timezone);
    if (error) {
      notify('error', error);
      return;
    }
    setSubmitting(true);
    const request = editingUuid
      ? api_with_method(
          'put',
          get_api_url(`${AFFILIATES_URL}/${editingUuid}`, false, true),
          payload,
          true
        )
      : api_with_method('post', get_api_url(AFFILIATES_URL, false, true), payload, true);
    request
      .then(() => {
        notify('success', editingUuid ? 'Affiliate updated.' : 'Affiliate created.');
        setSubmitting(false);
        close_form();
        load_affiliates();
      })
      .catch((e) => {
        notify('error', error_message(e));
        setSubmitting(false);
      });
  };

  const q = search.trim().toLowerCase();
  const filtered = affiliates.filter(
    (a) =>
      !q ||
      (a.name || '').toLowerCase().includes(q) ||
      (a.uuid || '').toLowerCase().includes(q) ||
      (a.funnel_url || '').toLowerCase().includes(q)
  );

  const copy_uuid = async (uuid) => {
    try {
      await navigator.clipboard.writeText(uuid);
      setCopiedUuid(uuid);
      setTimeout(() => setCopiedUuid((current) => (current === uuid ? '' : current)), 1500);
    } catch (err) {
      // Clipboard writes fail on insecure origins or a denied permission; the toast
      // names the failure and the uuid text stays selectable as the fallback.
      notify('error', `Could not copy (${err.name}). Select the ID and copy it manually.`);
    }
  };

  const render_copy = (uuid) => {
    const copied = copiedUuid === uuid;
    return (
      <button
        type="button"
        className={`aff-icon-btn ${copied ? 'is-copied' : ''}`}
        aria-label={copied ? 'Copied' : `Copy ID ${uuid}`}
        title={copied ? 'Copied' : 'Copy ID'}
        onClick={() => copy_uuid(uuid)}
      >
        {copied ? <Check size={14} /> : <Copy size={14} />}
      </button>
    );
  };

  const render_discount = (a) => {
    const parts = [];
    if (a.percentage_discount) parts.push(`${a.percentage_discount}%`);
    if (a.fixed_discount) parts.push(`${a.fixed_discount} off`);
    if (parts.length === 0) return <span className="aff-sub">No discount</span>;
    return <span className="aff-badge aff-badge-discount">{parts.join(' + ')}</span>;
  };

  const render_expiry = (a) => {
    if (!a.offer_expiry) return <span className="aff-muted">No end date</span>;
    const expiry = moment(a.offer_expiry).tz(iana_timezone);
    return (
      <div className="flex flex-col gap-1">
        <span className="aff-num aff-date">{expiry.format('DD MMM YYYY')}</span>
        <span className="aff-sub aff-num">{expiry.format('hh:mm A')}</span>
        {expiry.isBefore(moment()) ? (
          <span className="aff-badge aff-badge-expired self-start">Expired</span>
        ) : null}
      </div>
    );
  };

  const render_list = () => (
    <div className="aff-card flex-1 min-w-0 overflow-hidden">
      <div className="aff-toolbar">
        <div className="aff-search">
          <Search size={16} aria-hidden="true" />
          <input
            placeholder="Search by name, ID or funnel"
            aria-label="Search affiliates"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <span className="aff-count">
          {filtered.length} {filtered.length === 1 ? 'affiliate' : 'affiliates'}
        </span>
        <button type="button" className="aff-add" onClick={open_create}>
          <Plus size={16} aria-hidden="true" />
          Add Affiliate
        </button>
      </div>
      <div className="aff-table-wrap">
        <table className="aff-table">
          <thead>
            <tr>
              <th>Affiliate</th>
              <th>Credits</th>
              <th>Offer</th>
              <th>Funnel</th>
              <th>Offer ends</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {filtered.map((a) => (
              <tr key={a.uuid}>
                {/* The uuid sits under the name rather than in its own column: as a
                    separate column the 36-char uuid and the underscore names (no
                    break points) pushed Funnel, Offer ends and Edit off the card at
                    1440px. overflow-wrap anywhere lets an underscore name wrap. */}
                <td style={{ maxWidth: '300px', overflowWrap: 'anywhere' }}>
                  <div className="aff-name">{a.name}</div>
                  <div className="aff-uuid">
                    <span title={a.uuid}>{a.uuid}</span>
                    {render_copy(a.uuid)}
                  </div>
                </td>
                <td className="aff-num">{a.credits}</td>
                {/* Discount and class preference stack in one column: as two columns
                    they left the funnel about one word wide at 1024px. */}
                <td>
                  <div className="flex flex-col items-start gap-1">
                    {render_discount(a)}
                    <span className="aff-badge aff-badge-pref">
                      {CLASS_PREFERENCE_LABELS[a.class_preference] || a.class_preference}
                    </span>
                  </div>
                </td>
                <td style={{ minWidth: '150px', maxWidth: '220px', overflowWrap: 'anywhere' }}>
                  {a.funnel_url || <span className="aff-muted">None</span>}
                  {a.funnel_type ? <div className="aff-sub">{a.funnel_type}</div> : null}
                </td>
                <td>{render_expiry(a)}</td>
                <td>
                  <button
                    type="button"
                    className="aff-icon-btn aff-edit"
                    aria-label={`Edit ${a.name}`}
                    title="Edit"
                    onClick={() => open_edit(a)}
                  >
                    <Pencil size={14} aria-hidden="true" />
                    <span className="aff-edit-label">Edit</span>
                  </button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && !loading ? (
              <tr>
                <td className="aff-empty" colSpan={6}>
                  {q
                    ? `No affiliates match "${search.trim()}".`
                    : 'No affiliates yet. Add the first one.'}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );

  const render_field = ([key, label, type = 'text']) => (
    <CustomInput
      key={key}
      label={label}
      name={key}
      type={type}
      value={form[key]}
      onChange={on_change(key)}
      style={{ width: '100%' }}
    />
  );

  const render_form = () => (
    <CustomModal
      show={formOpen}
      close={close_form}
      title={editingUuid ? 'Edit Affiliate' : 'Add Affiliate'}
      inner_style={{ width: '90%', maxWidth: '900px' }}
    >
      {editingUuid ? (
        <div className="aff-form-uuid">
          <span>ID {editingUuid}</span>
          {render_copy(editingUuid)}
        </div>
      ) : null}
      {FORM_SECTIONS.map((section, index) => (
        <div
          key={section.title}
          className={`aff-section ${index === 0 ? 'aff-section-first' : ''}`}
        >
          <div className="aff-section-title">{section.title}</div>
          <div className="grid grid-cols-2 gap-x-4">
            {section.fields.map(render_field)}
            {section.title === 'Offer terms' ? (
              <>
                <CustomSelect
                  label="Class preference"
                  name="class_preference"
                  value={form.class_preference}
                  options={CLASS_PREFERENCE_OPTIONS}
                  onChange={on_change('class_preference')}
                  defaultOption="Select class preference"
                  style={{ width: '100%' }}
                />
                <CustomInput
                  label={`Offer ends (${iana_timezone}, optional)`}
                  name="offer_expiry"
                  type="datetime-local"
                  value={form.offer_expiry}
                  onChange={on_change('offer_expiry')}
                  style={{ width: '100%' }}
                />
              </>
            ) : null}
          </div>
        </div>
      ))}
      <div className="flex justify-end gap-3 mt-6">
        <Button type="secondary" onClick={close_form} disabled={submitting}>
          Cancel
        </Button>
        <Button onClick={submit} disabled={submitting}>
          {submitting ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </CustomModal>
  );

  if (!has_access) {
    return (
      <CommonHeader title="Affiliates" class_name="aff-page">
        <div className="aff-muted p-4">You do not have access to this page.</div>
      </CommonHeader>
    );
  }

  return (
    <CommonHeader loading={loading} title="Affiliates" class_name="aff-page">
      {render_list()}
      {formOpen ? render_form() : null}
    </CommonHeader>
  );
}
